import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import get from 'lodash/get';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { TooltipHost } from 'office-ui-fabric-react';
import DetailsList from '../DetailsList';
import DetailsListOverflowTooltip from '../DetailsListOverflowTooltip';
import StatusField from '../StatusField';
import { contractStatus } from '../../utils';
import styles from './ContractsList.style';

const ContractsList = ({
  items,
  className: classNameProp,
  sortColumn,
  sortAscending,
  handleColumnClick,
  ...other
}) => {
  const className = clsx(classNameProp);

  const columns = [
    {
      key: 'action',
      name: 'action',
      headerClassName: styles.iconColumnHeader,
      arianame: 'icon',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 8,
      maxWidth: 8,
      columnActionsMode: 0,
      onRender: (contract, index) => {
        return (
          <>
            {contract.approvalStatus !== 'Approved' && (
              <TooltipHost
                content="Action Required"
                aria-label="Action Required"
                calloutProps={{
                  gapSpace: 0,
                  target: `#${`actionIcon${index}`}`,
                }}
              >
                <div className={styles.actionIndicator}>
                  <FontIcon
                    iconName="StatusCircleExclamation"
                    aria-label="Action Required"
                    id={`actionIcon${index}`}
                  />
                </div>
              </TooltipHost>
            )}
          </>
        );
      },
    },
    {
      key: 'status',
      name: 'Status',
      headerClassName: styles.iconColumnHeader,
      iconClassName: styles.headerIcon,
      ariaLabel: 'Status',
      iconName: 'TextDocument',
      fieldName: 'status',
      minWidth: 60,
      maxWidth: 60,
      columnActionsMode: 0,
      onRender: (contract, index) => {
        const status = contractStatus(
          contract.effectiveDate,
          contract.terminationDate,
          contract.approvalStatus,
        );

        return <StatusField status={status} index={index} />;
      },
    },
    {
      key: 'name',
      name: 'Contract Name',
      fieldName: 'name',
      minWidth: 320,
      maxWidth: 320,
      isSorted: sortColumn === 'name',
      isSortedDescending: !sortAscending,
      onColumnClick: handleColumnClick,
    },
    {
      key: 'contractId',
      name: 'Contract ID',
      fieldName: 'contractId',
      minWidth: 185,
      maxWidth: 185,
      isSorted: sortColumn === 'contractId',
      isSortedDescending: !sortAscending,
      onColumnClick: handleColumnClick,
      onRender: contract => (
        <DetailsListOverflowTooltip
          ariaLabel="Contract ID"
          displayContent={contract.contractId}
          tooltipContent={contract.contractId}
        />
      ),
    },
    {
      key: 'partner',
      name: 'Partner',
      fieldName: 'partner',
      minWidth: 150,
      maxWidth: 150,
      isSorted: sortColumn === 'partner',
      isSortedDescending: !sortAscending,
      onColumnClick: handleColumnClick,
      onRender: contract => get(contract, 'partner.name', ''),
    },
  ];

  return (
    <DetailsList
      items={items.map(
        contract =>
          contract && {
            ...contract,
            key: contract.address,
            link: `/contracts/${contract.address}`,
          },
      )}
      columns={columns}
      className={className}
      {...other}
    />
  );
};

ContractsList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      contractId: PropTypes.string.isRequired,
      partner: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }),
      approvalStatus: PropTypes.string.isRequired,
    }),
  ),
  className: PropTypes.string,
  sortColumn: PropTypes.string,
  sortAscending: PropTypes.bool,
  handleColumnClick: PropTypes.func,
};

ContractsList.defaultProps = {
  items: [],
  className: '',
  sortColumn: '',
  sortAscending: false,
  handleColumnClick: () => {},
};

export default ContractsList;
